import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { ArrowBackSharp } from "@mui/icons-material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";
import { useEditor } from "../../components/contexts/EditorContext";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import FrontendAudioProcessor from "../../reactTimelineEditor/utils/FrontendAudioProcessor";

export default function EditorNavBar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const { currentUser } = useAuth();
  const { id, clipArray, videoRef, audioLength, videoData, processorRef, setIsProcessing } = useEditor();
  const navigate = useNavigate();
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    // Add the event listener
    window.addEventListener("scroll", handleScroll);

    // Clean up the event listener
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  async function calculateExport() {
    const video = videoRef?.current;
    if (!video) {
      message.error("Video player not available");
      return;
    }

    if (!video.captureStream) {
      message.error(
        "Export requires Chrome or Edge. Your browser doesn't support captureStream."
      );
      return;
    }

    setLoadingExport(true);
    setIsProcessing(true);
    setExportProgress(0);

    try {
      if (!processorRef.current) {
        processorRef.current = new FrontendAudioProcessor();
        await processorRef.current.init();
      }

      message.loading({
        content: "Exporting video...",
        key: "navExport",
        duration: 0,
      });

      const blob = await processorRef.current.exportWithEdits(
        video,
        clipArray,
        audioLength,
        (pct) => setExportProgress(pct)
      );

      // Create a download URL from the blob and navigate to export page
      const title = videoData?.video?.title || "export";
      const safeName = title.replace(/[^a-zA-Z0-9_-]/g, "_");
      const downloadUrl = URL.createObjectURL(blob);

      message.success({ content: "Export complete!", key: "navExport" });

      navigate(`/export`, {
        state: {
          url: downloadUrl,
          fileName: `${safeName}_edited.webm`,
        },
      });
    } catch (error) {
      console.error("Error exporting video:", error.message);
      message.error({ content: "Export failed: " + error.message, key: "navExport" });
    } finally {
      setLoadingExport(false);
      setIsProcessing(false);
      setExportProgress(0);
    }
  }

  const cancelExport = () => {
    if (videoRef?.current) {
      videoRef.current.pause();
    }
    setLoadingExport(false);
    setIsProcessing(false);
    setExportProgress(0);
    message.destroy();
    message.info("Export cancelled");
  };

  const formatEta = () => {
    if (exportProgress <= 0 || audioLength <= 0) return "";
    const remaining = audioLength * (1 - exportProgress);
    if (remaining < 60) return `~${Math.ceil(remaining)}s left`;
    return `~${Math.ceil(remaining / 60)}m left`;
  };

  return (
    <nav
      className={`fixed w-full z-10 ${
        isScrolled ? "bg-transparent" : "bg-slate-950 shadow-md"
      } transition-all duration-300 ease-linear`}
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between">
          <div className="flex space-x-4">
            {/* Logo section */}
            <div>
              <a
                href={`/videos/${id}`}
                className="flex items-center py-5 px-2 gap-5 justify-center align-center text-white "
              >
                <span className="font-bold">Zen Stream</span>
                <span className="font-bold hover:text-gray-400">
                  {" "}
                  <ArrowBackSharp /> Back To Video
                </span>
              </a>
            </div>
            {/* Primary Nav */}
            <div className="hidden md:flex items-center space-x-1"></div>
          </div>
          {/* Secondary Nav */}
          <div className="hidden md:flex items-center space-x-1">
            {currentUser ? (
              <>
                {loadingExport ? (
                  <div className="flex items-center gap-3 py-2 px-3 rounded-md bg-purple-600 text-white text-sm font-medium">
                    <div className="flex items-center gap-2 min-w-[160px]">
                      <div className="flex-1 h-1.5 bg-purple-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-white rounded-full transition-all duration-300"
                          style={{ width: `${Math.round(exportProgress * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono w-8">
                        {Math.round(exportProgress * 100)}%
                      </span>
                    </div>
                    <span className="text-purple-200 text-xs">{formatEta()}</span>
                    <button
                      onClick={cancelExport}
                      className="ml-1 p-0.5 rounded hover:bg-purple-500 transition-colors"
                      title="Cancel export"
                    >
                      <CloseIcon style={{ fontSize: 16 }} />
                    </button>
                  </div>
                ) : (
                  <p
                    onClick={() => calculateExport()}
                    className="py-2 px-3 cursor-pointer text-white bg-gray-300 bg-opacity-50 hover:bg-opacity-75 border border-gray-400 hover:border-gray-500 rounded-md shadow-sm hover:shadow transition duration-150 ease-in-out flex items-center gap-2"
                  >
                    <FileDownloadIcon style={{ fontSize: 18 }} />
                    Export
                  </p>
                )}
              </>
            ) : (
              <>
                <CircularProgress />
              </>
            )}
          </div>
          {/* Mobile button goes here */}
        </div>
      </div>
    </nav>
  );
}
